import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { apiRequest } from '../../Services/Common/ApiRequest';
import Global from '../../Services/Common/Global';
import { handleQuerySuccess } from '../../hooks/useQuriesSuccess';
import { mapParamsBySchema, mapRowsBySchema } from '../../util';
import { Home_Schema } from './Home_Schema';
import useSearchMutaion from '../../hooks/apis/useSearchMutaion';
import useSaveMutation from './../../hooks/apis/useSaveMutaion';

/** GetHomeData — 근로자의견 수렴 기간 플래그·홈 통합 데이터 */
export const useGetHomeDataMutation = (successOptions) => {
  return useSearchMutaion((context) => {
    const encryptedParams = mapParamsBySchema(
      Home_Schema.mapper.getHomeData,
      context
    );
    return apiRequest('post', Global.urls.Home.getHomeData, encryptedParams);
  }, successOptions);
};

/**
 * Home 화면 관련 React Query 훅 모음
 * - 공지사항 조회: useSearch1 (useQuery)
 * - 중대재해 처벌법 이행 알림판 조회: useSearch2 (useQuery)
 * - 안전뉴스 조회: useSearch3 (useQuery)
 * - 내 캘린더 조회: useSearch4 (useQuery)
 * - 달력 일정 조회: useSearch5 (useSearchMutaion)
 */

// 공지사항 조회
export const useSearch1 = (options = {}) => {
  const { onSuccess } = options;
  const custCd = useSelector((state) => state.SearchInfo.custCd);
  const coCd = useSelector((state) => state.SearchInfo.coCd);

  const query = useQuery({
    queryKey: ['home', 'getHomeDashboradR1', custCd, coCd],
    queryFn: () =>
      apiRequest(
        'post',
        Global.urls.Home.getHomeDashboradR1,
        mapParamsBySchema(Home_Schema.mapper.search1)
      ),
    enabled: true,
  });

  useEffect(() => {
    if (!query.isSuccess) return;

    handleQuerySuccess(query.data, {
      onSuccess: async (rows) => {
        await onSuccess?.(rows);
      },
    });
  }, [query.isSuccess, query.dataUpdatedAt]);

  return query;
};

// 알람 상세 조회
export const useSearch2 = (successOptions) => {
  return useSearchMutaion((context) => {
    const encryptedParams = mapParamsBySchema(
      Home_Schema.mapper.search2,
      context
    );
    return apiRequest(
      'post',
      Global.urls.Home.getHomeDashboradR2,
      encryptedParams
    );
  }, successOptions);
};

// 안전뉴스 조회
export const useSearch3 = (options = {}) => {
  const { onSuccess } = options;
  const params = mapParamsBySchema(Home_Schema.mapper.search3);

  const query = useQuery({
    queryKey: ['home', 'getNewsLetter', params],
    queryFn: () => apiRequest('post', Global.urls.Home.getNewsLetter, params),
    enabled: true,
  });

  useEffect(() => {
    if (!query.isSuccess) return;
    handleQuerySuccess(query.data, {
      onSuccess: async (rows) => {
        await onSuccess?.(rows);
      },
    });
  }, [query.isSuccess, query.dataUpdatedAt]);

  return query;
};

// 내 캘린더 조회
export const useSearch4 = (options = {}) => {
  const { onSuccess } = options;
  const params = mapParamsBySchema(Home_Schema.mapper.search4);

  const query = useQuery({
    queryKey: ['home', 'getCalendarR1', params],
    queryFn: () =>
      apiRequest('post', Global.urls.Calendar.getCalendarR1, params),
    enabled: true,
  });

  useEffect(() => {
    if (!query.isSuccess) return;
    handleQuerySuccess(query.data, {
      onSuccess: async (rows) => {
        await onSuccess?.(rows);
      },
    });
  }, [query.isSuccess, query.dataUpdatedAt]);

  return query;
};

// 달력 일정 조회 (월간 달력용)
export const useSearch5 = (successOptions) => {
  return useSearchMutaion((context) => {
    const encryptedParams = mapParamsBySchema(
      Home_Schema.mapper.search5,
      context
    );
    return apiRequest(
      'post',
      Global.urls.Calendar.getCalendarR2,
      encryptedParams
    );
  }, successOptions);
};
// 업무 목록 완료 내역
export const useSearch6 = (successOptions) => {
  return useSearchMutaion((context) => {
    const encryptedParams = mapParamsBySchema(
      Home_Schema.mapper.search6,
      context
    );
    return apiRequest(
      'post',
      Global.urls.Home.getHomeDashboradR3,
      encryptedParams
    );
  }, successOptions);
};

export const useSave1 = (options = {}) => {
  return useSaveMutation(async ({ rows }) => {
    const payload = mapRowsBySchema(rows, Home_Schema.mapper.save1)[0];

    const response = await apiRequest(
      'post1',
      Global.urls.Home.saveHomeDashboardU1,
      payload
    );
    return [response];
  }, options);
};

/**
 * 업무 목록 상태 변경 (U2)
 * save2: { rows }
 */
export const useSave2 = (options = {}) => {
  return useSaveMutation(
    async ({ rows }) => {
      const payload = mapRowsBySchema(rows, Home_Schema.mapper.save2)[0];
      return apiRequest('post1', Global.urls.Home.saveHomeDashboardU2, payload);
    },
    options,
    false
  );
};

/**
 * 업무 목록 우선 순위 변경 (U3)
 * save3: { rows }
 */
export const useSave3 = (options = {}) => {
  return useSaveMutation(
    async ({ rows }) => {
      const payload = mapRowsBySchema(rows, Home_Schema.mapper.save3)[0];
      return apiRequest('post1', Global.urls.Home.saveHomeDashboardU3, payload);
    },
    options,
    false
  );
};

/**
 * 알람 확인 처리 (U4)
 * save4: { rows }
 */
export const useSave4 = (options = {}) => {
  return useSaveMutation(async ({ rows }) => {
    const payload = mapRowsBySchema(rows, Home_Schema.mapper.save4)[0];
    return apiRequest('post1', Global.urls.Home.saveHomeDashboardU4, payload);
  }, options);
};

/**
 * 홈 대시보드 업무 삭제 (D1)
 * delete1: { rows }
 */
export const useDelete1 = (options = {}) => {
  return useSaveMutation(async ({ rows }) => {
    const payload = mapRowsBySchema(rows, Home_Schema.mapper.delete1)[0];
    const response = await apiRequest(
      'post1',
      Global.urls.Home.deleteHomeDashboardD1,
      payload
    );
    return [response];
  }, options);
};
